import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import usePersistedTab from '../../hooks/usePersistedTab';
import { 
    BarChart3, Users, Building2, FileText, PenTool, Star, 
    Download, RefreshCw, Mail, AlertCircle, CheckCircle,
    Euro, Activity
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

function StatCard({ title, value, subtitle, icon: Icon, color = "primary" }) {
    const colorClasses = {
        primary: "bg-[#681793]/10 text-[#681793]",
        orange: "bg-[#FF914D]/10 text-[#FF914D]",
        green: "bg-green-100 text-green-600",
        blue: "bg-blue-100 text-blue-600"
    };
    
    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground">{title}</p>
                        <p className="text-3xl font-bold mt-1">{value}</p>
                        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
                    </div>
                    <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function EventLog({ events }) {
    const eventLabels = {
        'signup_started': { label: 'Inscription', icon: Users, color: 'blue' },
        'email_confirmation_sent': { label: 'Email envoyé', icon: Mail, color: 'blue' },
        'email_confirmed': { label: 'Email confirmé', icon: CheckCircle, color: 'green' },
        'login_success': { label: 'Connexion', icon: Users, color: 'green' },
        'agency_created': { label: 'Agence créée', icon: Building2, color: 'purple' },
        'mandate_created': { label: 'Mandat créé', icon: FileText, color: 'orange' },
        'signature_completed': { label: 'Signature', icon: PenTool, color: 'green' },
        'rating_submitted': { label: 'Avis soumis', icon: Star, color: 'yellow' },
        'email_failed': { label: 'Email échoué', icon: AlertCircle, color: 'red' }
    };
    
    return (
        <div className="space-y-2">
            {events.map((event, idx) => {
                const config = eventLabels[event.type] || { label: event.type, icon: Activity, color: 'gray' };
                const IconComp = config.icon;
                
                return (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="p-2 rounded-full bg-gray-100">
                            <IconComp className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{config.label}</p>
                            <p className="text-xs text-muted-foreground truncate">
                                {event.user_id ? event.user_id.slice(0, 8) + '...' : 'N/A'} - {new Date(event.timestamp).toLocaleString('fr-FR')}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function SuperAdminStatsPage() {
    const [stats, setStats] = useState(null);
    const [events, setEvents] = useState([]);
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(null);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [token, setToken] = useState('');
    const [activeTab, setActiveTab] = usePersistedTab(
        'homly_superadmin_stats_tab',
        'events',
        ['events', 'emails', 'exports'],
    );

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const t = urlParams.get('token') || localStorage.getItem('superadmin_token');
        if (t) {
            setToken(t);
            loadData(t);
        }
    }, []);

    const loadData = async (authToken) => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${authToken}` };
            
            const [statsRes, eventsRes, emailsRes] = await Promise.all([
                api.get('/superadmin/statistics', { headers }),
                api.get('/superadmin/analytics-events?limit=20', { headers }),
                api.get('/superadmin/emails?limit=20', { headers })
            ]);
            
            setStats(statsRes.data);
            setEvents(eventsRes.data.events || []);
            setEmails(emailsRes.data.emails || []);
        } catch (error) {
            console.error('Error loading stats:', error);
            toast.error('Erreur de chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (type) => {
        setExporting(type);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const params = {};
            if (dateRange.start) params.start_date = dateRange.start;
            if (dateRange.end) params.end_date = dateRange.end;
            
            const response = await api.get(`/superadmin/export/${type}`, { headers, params });
            
            const data = response.data.data;
            if (!data || data.length === 0) {
                toast.error('Aucune donnée à exporter');
                return;
            }
            
            const csvHeaders = Object.keys(data[0]);
            const csvContent = [
                csvHeaders.join(','),
                ...data.map(row => csvHeaders.map(h => {
                    const val = row[h];
                    if (typeof val === 'object') return JSON.stringify(val).replace(/,/g, ';');
                    return `"${String(val || '').replace(/"/g, '""')}"`;
                }).join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `homly_${type}_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();
            
            toast.success(`Export ${type} téléchargé (${data.length} lignes)`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Erreur lors de l\'export');
        } finally {
            setExporting(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <BarChart3 className="w-7 h-7 text-[#681793]" />
                            Statistiques Plateforme
                        </h1>
                        <p className="text-muted-foreground">Vue d ensemble des KPIs Homly</p>
                    </div>
                    <Button onClick={() => loadData(token)} variant="outline">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard 
                        title="Agences" 
                        value={stats?.platform?.total_agencies || 0}
                        subtitle={`${stats?.platform?.active_agencies || 0} actives`}
                        icon={Building2}
                        color="primary"
                    />
                    <StatCard 
                        title="Utilisateurs" 
                        value={stats?.platform?.total_users || 0}
                        subtitle={`${stats?.platform?.pending_users || 0} en attente`}
                        icon={Users}
                        color="blue"
                    />
                    <StatCard 
                        title="Mandats" 
                        value={stats?.platform?.total_mandates || 0}
                        subtitle={`${stats?.platform?.active_mandates || 0} actifs`}
                        icon={FileText}
                        color="orange"
                    />
                    <StatCard 
                        title="MRR" 
                        value={`${stats?.revenue?.mrr?.toLocaleString() || 0} EUR`}
                        subtitle={`ARR: ${stats?.revenue?.arr?.toLocaleString() || 0} EUR`}
                        icon={Euro}
                        color="green"
                    />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Mail className="w-5 h-5" />
                                Acquisition
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Inscriptions</span>
                                <span className="font-medium">{stats?.acquisition?.signups || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Emails confirmes</span>
                                <span className="font-medium">{stats?.acquisition?.emails_confirmed || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Taux confirmation</span>
                                <span className="font-medium">{stats?.acquisition?.confirmation_rate || 0}%</span>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <PenTool className="w-5 h-5" />
                                Usage
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Mandats crees</span>
                                <span className="font-medium">{stats?.usage?.mandates_created || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Signatures envoyees</span>
                                <span className="font-medium">{stats?.usage?.signatures_sent || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Taux signature</span>
                                <span className="font-medium">{stats?.usage?.signature_completion_rate || 0}%</span>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Star className="w-5 h-5" />
                                Qualite
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Avis soumis</span>
                                <span className="font-medium">{stats?.quality?.ratings_submitted || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Avis confirmes</span>
                                <span className="font-medium">{stats?.quality?.ratings_confirmed || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Taux litiges</span>
                                <span className="font-medium">{stats?.quality?.dispute_rate || 0}%</span>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="events">Evenements recents</TabsTrigger>
                        <TabsTrigger value="emails">Emails</TabsTrigger>
                        <TabsTrigger value="exports">Exports</TabsTrigger>
                    </TabsList>

                    <TabsContent value="events">
                        <Card>
                            <CardHeader>
                                <CardTitle>Journal d activite</CardTitle>
                                <CardDescription>Les 20 derniers evenements de la plateforme</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {events.length > 0 ? (
                                    <EventLog events={events} />
                                ) : (
                                    <p className="text-center text-muted-foreground py-8">Aucun evenement</p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="emails">
                        <Card>
                            <CardHeader>
                                <CardTitle>Emails envoyes</CardTitle>
                                <CardDescription>Historique des emails de confirmation et reset</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {emails.length > 0 ? (
                                    <EventLog events={emails} />
                                ) : (
                                    <p className="text-center text-muted-foreground py-8">Aucun email</p>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="exports">
                        <Card>
                            <CardHeader>
                                <CardTitle>Exporter les donnees</CardTitle>
                                <CardDescription>Telechargez les donnees au format CSV</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex gap-4 items-end">
                                    <div className="space-y-2">
                                        <Label>Date debut</Label>
                                        <Input 
                                            type="date" 
                                            value={dateRange.start}
                                            onChange={(e) => setDateRange(d => ({ ...d, start: e.target.value }))}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Date fin</Label>
                                        <Input 
                                            type="date" 
                                            value={dateRange.end}
                                            onChange={(e) => setDateRange(d => ({ ...d, end: e.target.value }))}
                                        />
                                    </div>
                                    <Button 
                                        variant="outline"
                                        onClick={() => setDateRange({ start: '', end: '' })}
                                    >
                                        Reinitialiser
                                    </Button>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {[
                                        { key: 'users', label: 'Utilisateurs', icon: Users },
                                        { key: 'agencies', label: 'Agences', icon: Building2 },
                                        { key: 'mandates', label: 'Mandats', icon: FileText },
                                        { key: 'signatures', label: 'Signatures', icon: PenTool },
                                        { key: 'ratings', label: 'Avis', icon: Star },
                                        { key: 'analytics', label: 'Evenements', icon: Activity }
                                    ].map((item) => (
                                        <Button
                                            key={item.key}
                                            variant="outline"
                                            onClick={() => handleExport(item.key)}
                                            disabled={exporting === item.key}
                                            className="h-auto py-4 flex flex-col items-center gap-2"
                                        >
                                            {exporting === item.key ? (
                                                <RefreshCw className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <item.icon className="w-5 h-5" />
                                            )}
                                            <span>{item.label}</span>
                                            <Download className="w-4 h-4 text-muted-foreground" />
                                        </Button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
